import React, { useState } from "react";
import { Link } from "react-router-dom";
import Content from "@/components/specific/Content";
import { useDocumentTitle } from "@/utils/hooks/useDocumentTitle";
import { postPengaduan } from "@/utils/apis/public";

const INITIAL_FORM = {
  nomor_sambung: "",
  kode_perihal: "",
  nama_pelanggan: "",
  no_hp: "",
  alamat: "",
  keluhan: "",
};

const REQUIRED_FIELDS = {
  nama_pelanggan: "Nama pelanggan",
  no_hp: "Nomor handphone",
  alamat: "Alamat",
  keluhan: "Keluhan",
};

function FieldError({ message }) {
  if (!message) return null;
  return <p className="field-error text-xs text-red-500 mt-1">{message}</p>;
}

function FormField({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      <FieldError message={error} />
    </div>
  );
}

const inputClass = (error) =>
  `w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#095393] focus:border-transparent transition-colors ${
    error ? "border-red-400 bg-red-50" : "border-gray-300 bg-white"
  }`;

function Whistleblower() {
  useDocumentTitle("Pengaduan - Perumda Air Minum Cilegon Mandiri");

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    Object.entries(REQUIRED_FIELDS).forEach(([key, label]) => {
      if (!form[key] || String(form[key]).trim() === "") {
        newErrors[key] = `${label} wajib diisi`;
      }
    });

    if (form.no_hp && !/^\d{8,15}$/.test(form.no_hp)) {
      newErrors.no_hp = "Nomor handphone tidak valid (8-15 digit)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      const firstErrorEl = document.querySelector(".field-error");
      if (firstErrorEl)
        firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const payload = {};
      Object.entries(form).forEach(([key, value]) => {
        if (value !== "") payload[key] = value;
      });

      const response = await postPengaduan(payload);
      setSubmitStatus("success");
      setSubmitMessage(
        response?.data?.message ||
          "Pengaduan Anda berhasil dikirim. Kami akan segera menindaklanjuti.",
      );
      setForm(INITIAL_FORM);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitStatus("error");
      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan. Silakan coba lagi.";
      setSubmitMessage(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <div
        className="relative bg-[#095393] overflow-hidden w-screen z-0"
        style={{
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          width: "100vw",
          maxWidth: "100vw",
        }}
      >
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            className="w-full h-16 md:h-24"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,60 C300,100 600,20 900,60 C1050,80 1150,40 1200,60 L1200,120 L0,120 Z"
              fill="white"
              opacity="0.3"
            />
            <path
              d="M0,80 C300,120 600,40 900,80 C1050,100 1150,60 1200,80 L1200,120 L0,120 Z"
              fill="white"
              opacity="0.5"
            />
            <path
              d="M0,100 C300,140 600,60 900,100 C1050,120 1150,80 1200,100 L1200,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Pengaduan
            </h1>
            <div className="flex items-center justify-center gap-2 text-white/90 text-sm md:text-base">
              <Link to="/" className="hover:text-white transition-colors">
                Home
              </Link>
              <span className="text-white/60">/</span>
              <span className="text-white">Pengaduan</span>
            </div>
          </div>
        </div>
      </div>

      <Content showContentHeader={false}>
        <div className="w-full max-w-3xl mx-auto px-0 md:px-6 lg:px-8 -mt-8 md:-mt-12 relative z-20 pb-12">
          {/* Alert Status */}
          {submitStatus === "success" && (
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 flex-shrink-0 mt-0.5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-semibold text-sm">Pengaduan Terkirim!</p>
                <p className="text-sm mt-0.5">{submitMessage}</p>
              </div>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 flex-shrink-0 mt-0.5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-semibold text-sm">Pengiriman Gagal</p>
                <p className="text-sm mt-0.5">{submitMessage}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-5">
              {/* Section Header */}
              <div className="flex items-center gap-3 mb-5 pb-3 border-b border-gray-100">
                <div className="bg-[#095393]/10 p-2 rounded-lg text-[#095393]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                    />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-gray-800">
                  Data Pengaduan
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Nama Pelanggan"
                  required
                  error={errors.nama_pelanggan}
                >
                  <input
                    type="text"
                    name="nama_pelanggan"
                    value={form.nama_pelanggan}
                    onChange={handleChange}
                    placeholder="Masukkan nama lengkap"
                    className={inputClass(errors.nama_pelanggan)}
                  />
                </FormField>

                <FormField
                  label="Nomor Handphone"
                  required
                  error={errors.no_hp}
                >
                  <input
                    type="tel"
                    name="no_hp"
                    value={form.no_hp}
                    onChange={handleChange}
                    placeholder="08xxxxxxxxxx"
                    className={inputClass(errors.no_hp)}
                  />
                </FormField>

                <FormField
                  label="Nomor Sambung (opsional)"
                  error={errors.nomor_sambung}
                >
                  <input
                    type="text"
                    name="nomor_sambung"
                    value={form.nomor_sambung}
                    onChange={handleChange}
                    placeholder="Masukkan nomor sambung"
                    className={inputClass(errors.nomor_sambung)}
                  />
                </FormField>

                <FormField
                  label="Kode Perihal (opsional)"
                  error={errors.kode_perihal}
                >
                  <input
                    type="text"
                    name="kode_perihal"
                    value={form.kode_perihal}
                    onChange={handleChange}
                    placeholder="Masukkan kode perihal"
                    className={inputClass(errors.kode_perihal)}
                  />
                </FormField>

                <div className="md:col-span-2">
                  <FormField label="Alamat" required error={errors.alamat}>
                    <textarea
                      name="alamat"
                      value={form.alamat}
                      onChange={handleChange}
                      placeholder="Masukkan alamat lengkap"
                      rows={3}
                      className={`${inputClass(errors.alamat)} resize-none`}
                    />
                  </FormField>
                </div>

                <div className="md:col-span-2">
                  <FormField label="Keluhan" required error={errors.keluhan}>
                    <textarea
                      name="keluhan"
                      value={form.keluhan}
                      onChange={handleChange}
                      placeholder="Jelaskan keluhan atau pengaduan Anda secara detail"
                      rows={5}
                      className={`${inputClass(errors.keluhan)} resize-none`}
                    />
                  </FormField>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="flex-1 text-center py-3 px-6 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors no-underline"
              >
                Batal
              </Link>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-[#095393] hover:bg-[#074a7a] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Mengirim...
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                    Kirim Pengaduan
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </Content>
    </>
  );
}

export default Whistleblower;
